import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-asahi-otp-modal',
  imports: [CommonModule, FormsModule,TranslatePipe],
  templateUrl: './asahi-otp-modal.component.html',
  styleUrl: './asahi-otp-modal.component.scss'
})
export class AsahiOtpModalComponent {
@Input() visible = false;
@Output() resendOtp = new EventEmitter<void>();
@Input() phoneMasked = '958XXXXXXX';
@Input() countryCode: string = '84';
@Input() mobileNo: string = '';
@Input() asahi!:boolean;
@Input() errorMsg: string = '';
@Input() loading: boolean = false;
@Output() verified = new EventEmitter<string>();
@Output() closed = new EventEmitter<void>();
@Output() edit = new EventEmitter<void>();
@Output() zaloVerify = new EventEmitter<void>();
@Output() callVerify = new EventEmitter<void>();
otp: string[] = ['', '', '', ''];
otpDigits = [0, 1, 2, 3];
resendCountdown = 60;
timer: any;
private resetOtpListener!: () => void;

/**
 * After the view initializes, set up a global window listener to reset OTP
 */
ngAfterViewInit() {
  this.resetOtpListener = () => this.resetOtp();
  window.addEventListener('resetOtpModal', this.resetOtpListener);
}
onKeyPress(event: KeyboardEvent): void {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode < 48 || charCode > 57) {
    event.preventDefault(); // ❌ block anything not a number
  }
}
/**
 * Detect changes in input properties
 * - Starts the resend timer when modal becomes visible
 * - Clears timer when modal is hidden
 */
ngOnChanges(changes: SimpleChanges) {
    if(this.visible){
  document.body.style.overflow = 'hidden';
  }
  if (changes['visible']) {
    if (changes['visible'].currentValue === true) {
      this.startResendTimer();
    } else if (changes['visible'].currentValue === false) {
      this.clearTimer();
    }
  }
}

/**
 * Cleanup timers and event listeners when component is destroyed
 */
ngOnDestroy() {
  document.body.style.overflow = 'auto';
  this.clearTimer();
  if (this.resetOtpListener) {
    window.removeEventListener('resetOtpModal', this.resetOtpListener);
  }
}

/** Resets OTP fields and clears error/loading state */
resetOtp() {
  this.otp = ['', '', '', ''];
  this.errorMsg = '';
  this.loading = false;
}

/** Clears the resend countdown timer */
clearTimer() {
  if (this.timer) {
    clearInterval(this.timer);
    this.timer = null;
  }
}

/** Starts the resend OTP countdown timer */
startResendTimer() {
  this.clearTimer();
  this.resendCountdown = 60;
  this.timer = setInterval(() => {
    if (this.resendCountdown > 0) {
      this.resendCountdown--;
    } else {
      this.clearTimer();
    }
  }, 1000);
}

/**
 * Handles user input in OTP fields
 * - Only allows numeric input
 * - Automatically focuses next input if available
 */
onOtpInput(index: number, event: any) {
  const value = event.target.value.replace(/[^0-9]/g, '');
  this.otp[index] = value;
  if (this.errorMsg) this.errorMsg = '';
  if (value && index < 3) {
    const next = document.querySelectorAll('input[maxlength="1"]')[index + 1] as HTMLElement;
    if (next) next.focus();
  }
}

/**
 * Handles keydown events in OTP inputs
 * - Moves focus backward on Backspace if current input is empty
 */
onOtpKeydown(index: number, event: KeyboardEvent) {
  if (event.key === 'Backspace' && !this.otp[index] && index > 0) {
    const prev = document.querySelectorAll('input[maxlength="1"]')[index - 1] as HTMLElement;
    if (prev) prev.focus();
  }
}

/** Checks if all OTP fields are filled with valid digits */
isOtpValid() {
  return this.otp.every(d => d.length === 1);
}

/** Emits the verified OTP if valid */
onVerifyOtp() {
  if (this.isOtpValid()) {
    this.loading = true;
    this.errorMsg = '';
    this.verified.emit(this.otp.join(''));
  }
}

/** Closes the modal and resets state */
close() {
  this.clearTimer();
  this.resendCountdown = 60;
  this.loading = false;
  this.errorMsg = '';
  this.resetOtp();
  this.closed.emit();
}

/** Handles user editing the mobile number */
onEdit() {
  this.clearTimer();
  this.edit.emit();
  this.close();
}

/** Resets OTP and restarts resend timer */
onResendCode() {
  this.otp = ['', '', '', ''];
  this.startResendTimer();
  this.errorMsg = '';
  this.resendOtp.emit(); 
}

/** Triggers Zalo verification and sets loading state */
onZaloVerify() {
  this.loading = true;
  this.errorMsg = '';
  this.zaloVerify.emit();
}

/** Triggers call verification and sets loading state */
onCallVerify() {
  this.loading = true;
  this.errorMsg = '';
  this.callVerify.emit();
}

/** Sets an error message and stops loading */
setError(message: string) {
  this.errorMsg = message;
  this.loading = false;
}

/** Updates loading state */
setLoading(isLoading: boolean) {
  this.loading = isLoading;
}

/**
 * Returns the masked phone number for display
 * - Shows first 3 digits and masks the rest with 'X'
 */
getMaskedPhoneNumber(): string {
  if (!this.mobileNo) return this.phoneMasked;
  const cleanPhone = this.mobileNo.replace(/\D/g, '');
  if (cleanPhone.length >= 3) {
    const firstThree = cleanPhone.substring(0, 3);
    const maskedPart = 'X'.repeat(cleanPhone.length - 3);
    return `${firstThree}${maskedPart}`;
  }
  return this.phoneMasked;
}
}
